import {
    faCircleQuestion,
    faFileInvoiceDollar,
    faFileShield,
    faUndo,
    faUserHeadset,
} from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { Alert, Col, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useBranding } from "../../contexts/branding";
import { useQueryParam } from "../../hooks/use-query-string";
import i18n from "../../utils/i18n";
import { Spacer } from "../components/spacer";
import { useProfile } from "../profile/queries";
import { Subscribe } from "../subscribe-external";
import { PaymentModal } from "../subscribe-external/payment-modal";
import MenuLink from "./components/menu-link";
import CurrentPlanCard from "./current-plan-card";
import { useResetPendingTransaction, useSubscription } from "./queries";
import "./styles.scss";
import SubscriptionDetail from "./subscription-details";
import SubscriptionSkeletonLoader from "./subscription-skeleton-loader";
import { SubscribeHeader } from "../subscribe-external/header";

export function Subscription() {
    const [paymentModal, setPaymentModal] = useState<boolean>(false);

    const { search } = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useState(new URLSearchParams(search));
    const uuid = useQueryParam("uuid", "");
    const tz = useQueryParam("timezone", "Central");

    const {
        product_id: productId,
        session_id: sessionId,
        success: paymentSuccessParam,
        cancel: paymentFailedParam,
    } = Object.fromEntries(searchParams.entries());

    const { data: profile } = useProfile(true);
    const { data: subscription, isLoading } = useSubscription(uuid, tz);
    const { isLoading: resetting, mutateAsync: resetTransaction } =
        useResetPendingTransaction();

    const { t } = useTranslation();
    const { paymentFAQ, refundPolicyUrl, privacyUrl } = useBranding();

    const PaymentSuccess = paymentSuccessParam === "true";
    const FailedFailed = paymentFailedParam === "true";

    const timezone = useMemo(() => {
        if (profile?.timezone && !isLoading) {
            return profile?.timezone || "Central";
        }
        return tz || "Central";
    }, [profile, tz, isLoading]);

    useEffect(() => {
        if (tz && uuid) {
            sessionStorage.setItem("timezone", tz);
            sessionStorage.setItem("uuid", uuid);
        }
    }, [tz, uuid]);

    useEffect(() => {
        const canceled = new URLSearchParams(search).get("cancel");
        if (canceled) {
            void resetTransaction();
        }
    }, [search, resetTransaction]);

    useEffect(() => {
        if (sessionId && (PaymentSuccess || FailedFailed)) {
            setPaymentModal(true);
        }
    }, [sessionId, PaymentSuccess, FailedFailed, productId]);

    const openURLNewTab = (url: string) => {
        if (!url) return;
        const targetUrl = i18n.language === "es" ? `${url}-es` : url;
        const newWindow = window.open(
            targetUrl,
            "_blank",
            "noopener,noreferrer"
        );
        if (newWindow) {
            newWindow.opener = null;
        }
    };

    const goToSupport = () => {
        navigate("/app/feedback-list");
    };

    if (isLoading || resetting) {
        return <SubscriptionSkeletonLoader />;
    }

    return (
        <Container className="container-subscription">
            <div className="subscription-wrapper">
                {subscription?.active ? (
                    <>
                        <SubscribeHeader />
                        <div className="w-full justify-content-start bg-white border food-border-rounded p-3 flex-column subscription-header-wrapper">
                            <Col className="d-flex justify-content-between subscription-header">
                                <h4>
                                    {t("subscriptions.manage_subscriptions")}
                                </h4>
                            </Col>
                            <Spacer />
                            <h5 className="ml-3 mt-2 mb-0 current-subscription-heading">
                                {t("subscriptions.current_subscription")}
                            </h5>
                            <Col>
                                <CurrentPlanCard
                                    subscription={subscription}
                                    timezone={timezone}
                                />
                            </Col>
                            <Col>
                                <SubscriptionDetail
                                    subscription={subscription}
                                    timezone={timezone}
                                />
                            </Col>

                            {["Apple", "Google"].includes(
                                subscription?.provider
                            ) &&
                                subscription.auto_renew && (
                                    <Col className="d-flex justify-content-center subscription-provider-warning">
                                        <Alert variant="warning width-100 mt-3 alert-auto-renew">
                                            <div>
                                                <FontAwesomeIcon
                                                    icon={faTriangleExclamation}
                                                    className="icon"
                                                />
                                            </div>
                                            <div>
                                                <span
                                                    style={{
                                                        fontWeight: "500",
                                                    }}
                                                >
                                                    {t("note")}
                                                    {": "}
                                                </span>
                                                {subscription?.provider ===
                                                "Apple"
                                                    ? t(
                                                          "subscriptions.stop_auto_renewal_apple"
                                                      )
                                                    : t(
                                                          "subscriptions.stop_auto_renewal_google"
                                                      )}
                                            </div>
                                        </Alert>
                                    </Col>
                                )}
                        </div>

                        <div className="w-full bg-white border food-border-rounded p-2 gap-2 flex-column mb-4">
                            <MenuLink
                                icon={faFileInvoiceDollar}
                                title={t("subscriptions.sub_billing_history")}
                                onClick={() =>
                                    navigate("/app/subscription/transactions")
                                }
                            />
                            <MenuLink
                                icon={faCircleQuestion}
                                title={t("subscriptions.billing_faq")}
                                onClick={() => openURLNewTab(paymentFAQ)}
                            />
                            <MenuLink
                                icon={faUndo}
                                title={t("subscriptions.refund_policy")}
                                onClick={() => openURLNewTab(refundPolicyUrl)}
                            />
                            <MenuLink
                                icon={faFileShield}
                                title={t("subscriptions.privacy_policy")}
                                onClick={() => openURLNewTab(privacyUrl)}
                            />
                            <MenuLink
                                icon={faUserHeadset}
                                title={t("subscriptions.contact_support")}
                                onClick={goToSupport}
                            />
                        </div>
                    </>
                ) : (
                    <Subscribe />
                )}
            </div>
            <PaymentModal onClose={setPaymentModal} open={paymentModal} />
        </Container>
    );
}
